import { Home, ClipboardList, Package, Stethoscope, Users, AlertTriangle, Building2, Rss, BarChart3, Radio, UserCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useFocusedPatient } from '@/hooks/useFocusedPatient';
import { useIsMobile } from '@/hooks/use-mobile';
import { useShift } from '@/hooks/useShift';
import { useCompany } from '@/hooks/useCompany';
import { usePatientRole } from '@/hooks/usePatientRole';

interface TabItem {
  icon: React.ElementType;
  label: string;
  path: string;
  matchPaths?: string[];
}

export function BottomTabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { focusedPatient, setFocusedPatientId } = useFocusedPatient();
  const isMobile = useIsMobile();
  const { activeShift } = useShift();
  const { isCompanyManagerOrCoordinator } = useCompany();
  const { isCaregiver, canViewDashboard } = usePatientRole(focusedPatient?.id);

  const shiftInOtherPatient = activeShift && focusedPatient && activeShift.patient_id !== focusedPatient.id;

  // Hide on desktop - sidebar is used instead
  if (!isMobile) {
    return null;
  }

  let tabs: TabItem[];

  if (isCompanyManagerOrCoordinator) {
    // Manager/coordinator: org-level shortcuts (always visible, ignores focused patient)
    tabs = [
      { icon: Building2, label: t('analytics.teamTab'), path: '/app/company', matchPaths: ['/app/company?tab=team', '/app/company'] },
      { icon: Radio, label: t('live.tab'), path: '/app/company?tab=live' },
      { icon: BarChart3, label: t('analytics.tab'), path: '/app/company?tab=analytics' },
      { icon: Users, label: t('nav.myPatients'), path: '/app/patients' },
    ];
  } else if (!focusedPatient) {
    // Non-manager without focused patient
    tabs = [
      { icon: Home, label: t('nav.home'), path: '/app' },
      { icon: Users, label: t('nav.myPatients'), path: '/app/patients' },
    ];
  } else if (isCaregiver) {
    // Caregiver with focused patient
    tabs = [
      { icon: Stethoscope, label: t('nav.caregiverMode'), path: `/app/caregiver/${focusedPatient.id}` },
      { icon: Package, label: t('nav.inventory'), path: `/app/inventory/${focusedPatient.id}` },
      { icon: UserCircle, label: t('nav.panel'), path: `/app/patients/${focusedPatient.id}` },
      { icon: Users, label: t('nav.changePatient'), path: '/app/patients' },
    ];
  } else {
    // Family (owner/observer) with focused patient
    tabs = [
      { icon: Rss, label: t('nav.followPatient'), path: `/app/family/${focusedPatient.id}` },
      { icon: ClipboardList, label: t('carePlan.title'), path: `/app/patients/${focusedPatient.id}/care-plan` },
      { icon: Package, label: t('nav.inventory'), path: `/app/inventory/${focusedPatient.id}` },
      { icon: Users, label: t('nav.changePatient'), path: '/app/patients' },
    ];
  }

  const isTabActive = (tab: TabItem) => {
    const [tabPath, tabQuery] = tab.path.split('?');
    const currentPath = location.pathname;
    const currentSearch = location.search.replace(/^\?/, '');

    if (tabQuery) {
      // Match exact path + query param
      const tabParams = new URLSearchParams(tabQuery);
      const currentParams = new URLSearchParams(currentSearch);
      if (currentPath !== tabPath) return false;
      for (const [k, v] of tabParams.entries()) {
        if (currentParams.get(k) !== v) return false;
      }
      return true;
    }

    // For /app/company without query, only match when no tab param or tab=team
    if (tabPath === '/app/company' && currentPath === '/app/company') {
      const currentTab = new URLSearchParams(currentSearch).get('tab');
      return !currentTab || currentTab === 'team';
    }

    // /app/patients should match exactly (not /app/patients/:id which is the patient panel)
    if (tabPath === '/app/patients') {
      return currentPath === tabPath;
    }

    return currentPath === tabPath || currentPath.startsWith(tabPath + '/');
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 safe-area-bottom">
      {/* Active shift in other patient banner */}
      {shiftInOtherPatient && (
        <button
          onClick={() => {
            setFocusedPatientId(activeShift.patient_id);
            navigate(`/app/caregiver/${activeShift.patient_id}`);
          }}
          className="w-full flex items-center justify-center gap-2 bg-warning/90 text-warning-foreground py-2 px-4 text-sm font-medium"
        >
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span className="truncate">{t('shift.activeElsewhereBanner')}</span>
          <span className="shrink-0 text-xs underline">{t('shift.tapToSwitch')}</span>
        </button>
      )}
      {/* Navigation Bar */}
      <nav className="bg-card border-t border-border" data-tour="app-navigation">
        <div className="flex items-stretch justify-around min-h-[76px] px-1 py-1">
          {tabs.map((tab) => (
            <TabButton
              key={tab.path}
              tab={tab}
              isActive={isTabActive(tab)}
              onClick={() => navigate(tab.path)}
            />
          ))}
        </div>
      </nav>
    </div>
  );
}

interface TabButtonProps {
  tab: TabItem;
  isActive: boolean;
  onClick: () => void;
}

function TabButton({ tab, isActive, onClick }: TabButtonProps) {
  const Icon = tab.icon;

  return (
    <button
      onClick={onClick}
      className={cn(
        "flex-1 flex flex-col items-center justify-start gap-1 px-1 py-1.5 rounded-lg transition-colors max-w-[88px]",
        isActive
          ? "text-primary"
          : "text-muted-foreground hover:text-foreground"
      )}
    >
      <Icon className="w-5 h-5 shrink-0" />
      <span className="text-[11px] font-medium leading-tight text-center break-words hyphens-auto">{tab.label}</span>
    </button>
  );
}
